import {FormikErrors} from 'formik';
import {Box, Typography} from '@mui/material';
import React from 'react';
import {OVRecommendActionEnum} from '../../../schema-types/globalTypes';
import {actionValidator} from '../useSaveBuilder/saveAction';
import {modifyValidator} from '../useSaveBuilder/saveModify';
import {contributeValidator} from '../BuilderContribute/builderContributeValidators';
import {setUpNewValidator} from '../BuilderSetUpNew/builderSetUpNewValidator';
import {protectionPlanValidator} from '../BuilderSetUpNew/builderProtectionPlanValidator';
import {annuityValidator} from '../BuilderSetUpNew/builderAnnuityValidator';
import {transferValidator} from '../BuilderTransfer/builderTransferValidator';
import {withdrawalValidator} from '../BuilderWithdrawal/builderWithdrawalValidators';
import {builderFieldsType, PlanActionFieldKeys} from './builderFields';
import {camelCaseToCapitalisedString} from '../../../utils/camelCaseToCapitalisedString';
import {flattenObject} from '../../../utils/flattenObject';
import {uuidRegex} from '../../../components/PrecursorForm/uuidRegex';
import {adHocFeeValidator} from '../BuilderAdHocFee/builderAdHocFeeValidator';

export const builderValidation: (
  values: builderFieldsType
) => FormikErrors<builderFieldsType> = (values) => {
  const errors: FormikErrors<builderFieldsType> = {};

  if (values.planAction === OVRecommendActionEnum.WITHDRAW) {
    withdrawalValidator(values, errors);
  }
  if (values.planAction === OVRecommendActionEnum.MODIFY) {
    modifyValidator(values, errors);
  }
  if (values.planAction === OVRecommendActionEnum.ACTION) {
    actionValidator(values, errors);
  }
  if (values.planAction === OVRecommendActionEnum.CONTRIBUTE) {
    contributeValidator(values, errors);
  }
  if (values.planAction === OVRecommendActionEnum.TRANSFER) {
    transferValidator(values, errors);
  }
  if (values.planAction === OVRecommendActionEnum.SET_UP_NEW) {
    if (values.mainPlanType === 'protectionPlan') {
      protectionPlanValidator(values, errors);
    } else if (values.mainPlanType === 'annuity') {
      annuityValidator(values, errors);
    } else {
      setUpNewValidator(values, errors);
    }
  }
  if (values.planAction === OVRecommendActionEnum.AD_HOC_FEE) {
    adHocFeeValidator(values, errors);
  }
  return errors;
};
const planActionKeys: PlanActionFieldKeys[] = [
  'setUpNew',
  'modify',
  'contribute',
  'transfer',
  'withdraw',
  'action',
  'adHocFee',
];

const removeUnnecessaryKeys = (keys: (string | PlanActionFieldKeys)[]) =>
  keys
    .filter(
      // removing top level keys if they're plan action
      (key, index) => {
        if (planActionKeys.includes(key as PlanActionFieldKeys)) {
          if (index === 0) {
            return false;
          }
        }
        return true;
      }
    )
    .map((key) => {
      // replacing keys that are hard to understand
      const output = key
        .replace('subplan0', 'firstSubplan')
        .replace('subplan1', 'secondSubplan')
        .replace('shell-0', 'Source plan') // hacky but im not wasting time on something clever
        .replace('shell-1', 'Source plan #1')
        .replace('shell-2', 'Source plan #2')
        .replace('shell-3', 'Source plan #3')
        .replace('shell-4', 'Source plan #4')
        .replace('transferSources', '')
        .replace('Transfer sources >', '')
        .replace(uuidRegex, 'Plan')
        .replace('legacy', '');

      return output;
    });

export const formatBuilderNestedKeys = (key: string) => {
  const keys = key.replace('action.note', 'note').split('.');
  return removeUnnecessaryKeys(keys)
    .filter((x) => x) // remove empty strings
    .map((txt) => camelCaseToCapitalisedString(txt))
    .join(' ➡️ ')
    .replace('Dfmfee id', 'DFM fee')
    .replace('Product fee id', 'Product fee')
    .replace('Fpfee id', 'FP fee');
};

export const getListOfErrors = <T extends {}>(
  errors: FormikErrors<T>,
  formatKey: (key: string) => string
): string[] => {
  const flatErrors = flattenObject(errors);
  const payload: string[] = [];
  for (const [key, value] of Object.entries(flatErrors)) {
    payload.push(`${formatKey(key)}: ${value}`);
  }
  return payload;
};
export const listErrorMessagesAsNodes = (messages: string[]) => {
  return (
    <Box display="flex" flexDirection="column">
      <Typography>Form not valid. Please check fields below:</Typography>
      <ul>
        {messages.map((m) => (
          <li key={m}>{m}</li>
        ))}
      </ul>
    </Box>
  );
};
